/**
 * Primary navigation rail. Renders entirely from `nav-config` (single source
 * of truth), matching docs/sidebar-mockup.html: pinned items, collapsible
 * sections, the nested Biometrics subgroup, and a pinned Settings footer.
 *
 * Behaviour: active highlighting via `NavLink` (+ `aria-current`), per-section
 * collapse persisted by `useSidebarState`, conditional visibility via
 * `useNavVisibility`, and an icon-only compact mode with tooltips.
 */
import { Link, NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  FOOTER,
  PINNED,
  SECTIONS,
  isSubgroup,
  type NavLeaf,
  type NavSection,
  type NavSubgroup,
} from "./nav-config";
import { useNavVisibility } from "./use-nav-visibility";

interface SidebarProps {
  compact: boolean;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Live badge counts by `badgeKey`. Absent ⇒ no badge (counts wired later). */
  badges?: Record<string, number>;
  /** Mobile drawer: close on navigate. */
  onNavigate?: () => void;
}

/** Desktop rail — sticky, gradient navy, hidden below `lg`. Width animates
 *  between full and compact. */
export function Sidebar(props: SidebarProps) {
  return (
    <aside
      className={cn(
        "sidebar sticky top-0 hidden h-screen shrink-0 flex-col text-white transition-[width] duration-200 lg:flex",
        "bg-[radial-gradient(circle_at_20%_35%,rgba(37,99,235,0.32),transparent_28%),radial-gradient(circle_at_80%_85%,rgba(6,182,212,0.18),transparent_30%),linear-gradient(180deg,#081225_0%,#0b1325_55%,#071021_100%)]",
        props.compact ? "w-[74px]" : "w-[248px]",
      )}
    >
      <SidebarContents {...props} />
    </aside>
  );
}

/** Brand + nav + footer, with no outer chrome — shared by the desktop rail and
 *  the mobile drawer. */
export function SidebarContents(props: SidebarProps) {
  return (
    <>
      <Brand compact={props.compact} />
      <nav
        aria-label="Primary"
        className="sidebar-scroll flex-1 overflow-y-auto px-3 pb-2 pt-2.5"
      >
        <SidebarNav {...props} />
      </nav>
      <div className="border-t border-white/[0.06] px-3 py-2.5">
        {FOOTER.map((leaf) => (
          <NavItemLink
            key={leaf.id}
            leaf={leaf}
            compact={props.compact}
            onNavigate={props.onNavigate}
          />
        ))}
      </div>
    </>
  );
}

function SidebarNav({ compact, isOpen, toggle, badges, onNavigate }: SidebarProps) {
  const vis = useNavVisibility();

  return (
    <>
      {/* Pinned */}
      <div className="mb-1.5 border-b border-white/[0.06] pb-2">
        {PINNED.filter(vis.canSeeLeaf).map((leaf) => (
          <NavItemLink
            key={leaf.id}
            leaf={leaf}
            compact={compact}
            badge={leaf.badgeKey ? badges?.[leaf.badgeKey] : undefined}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {/* Sections */}
      {(SECTIONS as NavSection[])
        .filter((s) => !s.requiresSuperAdmin || vis.isSuperAdmin)
        .map((section) => (
          <SectionGroup
            key={section.id}
            section={section}
            compact={compact}
            open={isOpen(section.id)}
            onToggle={() => toggle(section.id)}
            isOpen={isOpen}
            toggle={toggle}
            badges={badges}
            onNavigate={onNavigate}
          />
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------
function Brand({ compact }: { compact: boolean }) {
  return (
    <Link
      to="/"
      aria-label="Go to dashboard"
      className={cn(
        "flex items-center gap-3 border-b border-white/[0.06] px-4 pb-4 pt-[18px] transition-opacity hover:opacity-80",
        compact && "justify-center px-0",
      )}
    >
      <img
        src="/logo-icon.png"
        alt=""
        aria-hidden="true"
        className="h-8 w-8 shrink-0"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
      {!compact && (
        <div className="leading-tight">
          <p className="text-[15px] font-semibold tracking-tight text-white">
            HRIS Workbench
          </p>
          <p className="text-[11px] font-medium text-white/55">People · Purpose</p>
        </div>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
function SectionGroup({
  section,
  compact,
  open,
  onToggle,
  isOpen,
  toggle,
  badges,
  onNavigate,
}: {
  section: NavSection;
  compact: boolean;
  open: boolean;
  onToggle: () => void;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  badges?: Record<string, number>;
  onNavigate?: () => void;
}) {
  const vis = useNavVisibility();
  // Items visible after entitlement/role filtering.
  const items = section.items.filter((item) =>
    isSubgroup(item) ? vis.canSeeSubgroup(item) : vis.canSeeLeaf(item),
  );
  if (items.length === 0) return null;

  return (
    <div className={cn("mt-1.5", compact && "mt-0.5 border-t border-white/[0.05] pt-0.5")}>
      {/* Header hidden in compact mode (mockup) */}
      {!compact && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-1.5 rounded-md px-[11px] pb-1.5 pt-[9px] text-[11px] font-semibold uppercase tracking-[0.085em] text-white/[0.42] transition-colors hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
        >
          <span className="flex-1 truncate text-left">{section.label}</span>
          <ChevronDown
            size={15}
            aria-hidden="true"
            className={cn("shrink-0 opacity-80 transition-transform duration-200", open && "rotate-180")}
          />
        </button>
      )}

      {(compact || open) && (
        <div>
          {items.map((item) =>
            isSubgroup(item) ? (
              <SubgroupGroup
                key={item.id}
                group={item}
                compact={compact}
                open={isOpen(item.id)}
                onToggle={() => toggle(item.id)}
                badges={badges}
                onNavigate={onNavigate}
              />
            ) : (
              <NavItemLink
                key={item.id}
                leaf={item}
                compact={compact}
                badge={item.badgeKey ? badges?.[item.badgeKey] : undefined}
                onNavigate={onNavigate}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function SubgroupGroup({
  group,
  compact,
  open,
  onToggle,
  badges,
  onNavigate,
}: {
  group: NavSubgroup;
  compact: boolean;
  open: boolean;
  onToggle: () => void;
  badges?: Record<string, number>;
  onNavigate?: () => void;
}) {
  return (
    <div className={cn(!compact && "my-0.5 ml-1.5 border-l border-white/[0.09] pl-2")}>
      {!compact && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-[7px] text-[11px] font-semibold uppercase tracking-[0.07em] text-white/[0.42] transition-colors hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
        >
          <span className="flex-1 text-left">{group.label}</span>
          <span className="rounded-[4px] bg-purple/20 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-[0.08em] text-purple-200">
            module
          </span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={cn("shrink-0 opacity-75 transition-transform duration-200", open && "rotate-180")}
          />
        </button>
      )}
      {(compact || open) && (
        <div>
          {group.items.map((leaf) => (
            <NavItemLink
              key={leaf.id}
              leaf={leaf}
              compact={compact}
              badge={leaf.badgeKey ? badges?.[leaf.badgeKey] : undefined}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function NavItemLink({
  leaf,
  compact,
  badge,
  onNavigate,
}: {
  leaf: NavLeaf;
  compact: boolean;
  badge?: number;
  onNavigate?: () => void;
}) {
  const Icon = leaf.icon;
  return (
    <NavLink
      to={leaf.to}
      end={leaf.end}
      onClick={onNavigate}
      title={compact ? leaf.label : undefined}
      className={({ isActive }) =>
        cn(
          "group relative my-px flex items-center gap-[11px] rounded-[8px] px-[11px] py-2 text-[13.5px] font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan",
          isActive
            ? "bg-[#2563EB]/[0.18] font-semibold text-white"
            : "text-white/[0.66] hover:bg-white/[0.06] hover:text-white",
          compact && "justify-center gap-0 px-0 py-2.5",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute top-1.5 bottom-1.5 w-[3px] rounded-r-[3px] bg-blue",
                compact ? "left-0" : "-left-3",
              )}
            />
          )}
          <Icon
            size={20}
            strokeWidth={1.75}
            aria-hidden="true"
            className={cn("shrink-0", isActive ? "opacity-100" : "opacity-90")}
          />
          {!compact && <span className="flex-1 truncate">{leaf.label}</span>}
          {!compact && badge !== undefined && badge > 0 && (
            <span
              className={cn(
                "rounded-full px-[7px] py-[3px] text-[11px] font-semibold leading-none tabular-nums",
                isActive ? "bg-white text-blue" : "bg-blue text-white",
              )}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
